import { ApolloProvider } from "@apollo/client";
import type { AppProps } from "next/app";

import { GlobalStylesProvider } from "@zapier/design-system";
import { DesignSystemProvider } from "@zapier/design-system-context";
import { CurrentAccountIdProvider } from "@zapier/identity";
import * as Sentry from "@sentry/react";

import { useCurrentApolloClient } from "@/apollo";
import { Link, StylesReset, PageLayout } from "@/components";
import dynamic from "next/dynamic";
import {
  TrackingProvider,
  TrackRouting,
  initializeDatadogRum,
  nextJsReportWebVitals,
  initializeSentry,
} from "../observability";
import { initializeBuilderIOKit } from "@zapier/builder.io-kit";
import { Treatments, TreatmentsProvider } from "@/contexts/TreatmentsContext";
import { ctaCardConfig } from "@/components/builder/CTACard";
import {
  planCardCollectionConfig,
  taskSelectorConfig,
} from "@/components/builder";
import { pricingOptionsConfig } from "@/components/builder/PricingOptions";
import { planCardCollectionV2Config } from "@/components/builder/PlanCardCollectionV2";
import { popoverConfig } from "@/components/builder/Popover";
import { simplePlanCardCollectionConfig } from "@/components/builder/SimplePlanCardCollection";

initializeSentry({
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN!,
  commitHash: process.env.NEXT_PUBLIC_COMMIT_SHA,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
});

initializeBuilderIOKit({
  coreComponentsLoader: (componentName: string) =>
    dynamic(() =>
      import("@zapier/builder.io-kit").then(
        (mod: Record<string, any>) => mod?.[componentName]
      )
    ),
  Sentry,
  customComponents: [
    ctaCardConfig,
    planCardCollectionConfig,
    planCardCollectionV2Config,
    pricingOptionsConfig,
    popoverConfig,
    simplePlanCardCollectionConfig,
    taskSelectorConfig,
  ],
});

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
initializeDatadogRum();

export const reportWebVitals = nextJsReportWebVitals;

function App({
  Component,
  pageProps,
}: AppProps<Partial<{ treatments: Treatments }>>) {
  const apolloClient = useCurrentApolloClient(pageProps);
  const treatments = pageProps.treatments || {};

  return (
    <ApolloProvider client={apolloClient}>
      <StylesReset />
      <GlobalStylesProvider isPlatform={true} isRebrand={true}>
        <CurrentAccountIdProvider>
          <DesignSystemProvider LinkComponent={Link}>
            <TrackingProvider>
              <TrackRouting>
                <PageLayout>
                  <TreatmentsProvider initialValue={treatments}>
                    <Component {...pageProps} />
                  </TreatmentsProvider>
                </PageLayout>
              </TrackRouting>
            </TrackingProvider>
          </DesignSystemProvider>
        </CurrentAccountIdProvider>
      </GlobalStylesProvider>
    </ApolloProvider>
  );
}

export default App;
